import { ComponentType, createContext, Dispatch, ReactNode } from "react";

import { MAX_OPEN_PANELS } from "./constants";

/**
 * Props passed to side panel components
 */
export interface SidePanelProps {
    onClose: () => void;
}

/**
 * Configuration for a side panel
 */
export interface PanelConfig {
    id: string;
    title: string;
    icon: ReactNode;
    component: ComponentType<SidePanelProps>;
    /** When true, panel sizes to its content instead of stretching to fill available height */
    fitContent?: boolean;
    /**
     * When true, the panel's internal header (title, close, collapse buttons) is not rendered.
     * Use for SDK-registered windows that supply their own header inside the component.
     */
    hideHeader?: boolean;
}

/**
 * Side panels state
 */
export interface State {
    /** IDs of currently open panels, ordered by open time (oldest first) */
    openPanels: string[];
    /** Registry of all available panels */
    panelRegistry: Record<string, PanelConfig>;
    /** Whether panels are in collapsed (half-height) mode */
    collapsed: boolean;
}

export const initialState: State = {
    openPanels: [],
    panelRegistry: {},
    collapsed: false,
};

enum ActionTypes {
    RegisterPanel,
    UnregisterPanel,
    OpenPanel,
    ClosePanel,
    TogglePanel,
    CloseAllPanels,
    ToggleCollapsed,
}

function registerPanel(config: PanelConfig) {
    return {
        type: ActionTypes.RegisterPanel as const,
        config,
    };
}

function unregisterPanel(panelId: string) {
    return {
        type: ActionTypes.UnregisterPanel as const,
        panelId,
    };
}

function openPanel(panelId: string) {
    return {
        type: ActionTypes.OpenPanel as const,
        panelId,
    };
}

function closePanel(panelId: string) {
    return {
        type: ActionTypes.ClosePanel as const,
        panelId,
    };
}

function togglePanel(panelId: string) {
    return {
        type: ActionTypes.TogglePanel as const,
        panelId,
    };
}

function closeAllPanels() {
    return {
        type: ActionTypes.CloseAllPanels as const,
    };
}

function toggleCollapsed() {
    return {
        type: ActionTypes.ToggleCollapsed as const,
    };
}

export const actions = {
    registerPanel,
    unregisterPanel,
    openPanel,
    closePanel,
    togglePanel,
    closeAllPanels,
    toggleCollapsed,
};

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchSidePanels = Dispatch<Actions>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchSidePanels>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.RegisterPanel: {
            const panelId = action.config.id;

            if (state.panelRegistry[panelId]) {
                console.warn(`Panel "${panelId}" is already registered`);
                return state;
            }

            return {
                ...state,
                panelRegistry: {
                    ...state.panelRegistry,
                    [panelId]: action.config,
                },
            };
        }

        case ActionTypes.UnregisterPanel: {
            const { [action.panelId]: _removed, ...remainingPanels } = state.panelRegistry;

            return {
                ...state,
                panelRegistry: remainingPanels,
                // Also close the panel if it was open
                openPanels: state.openPanels.filter((id) => id !== action.panelId),
            };
        }

        case ActionTypes.OpenPanel: {
            const { panelId } = action;

            // Check if panel is registered
            if (!state.panelRegistry[panelId]) {
                console.warn(`Panel "${panelId}" is not registered`);
                return state;
            }

            // Check if already open
            if (state.openPanels.includes(panelId)) {
                return state;
            }

            let newOpenPanels = [...state.openPanels, panelId];

            // If we exceed max panels, remove the oldest (FIFO)
            if (newOpenPanels.length > MAX_OPEN_PANELS) {
                newOpenPanels = newOpenPanels.slice(-MAX_OPEN_PANELS);
            }

            return {
                ...state,
                openPanels: newOpenPanels,
                // Reset collapsed when opening a panel
                collapsed: false,
            };
        }

        case ActionTypes.ClosePanel: {
            return {
                ...state,
                openPanels: state.openPanels.filter((id) => id !== action.panelId),
                // Reset collapsed when closing a panel
                collapsed: false,
            };
        }

        case ActionTypes.TogglePanel: {
            const { panelId } = action;

            // Check if panel is registered
            if (!state.panelRegistry[panelId]) {
                console.warn(`Panel "${panelId}" is not registered`);
                return state;
            }

            const isOpen = state.openPanels.includes(panelId);

            if (isOpen) {
                // Close the panel
                return {
                    ...state,
                    openPanels: state.openPanels.filter((id) => id !== panelId),
                    // Reset collapsed when toggling
                    collapsed: false,
                };
            } else {
                // Open the panel (with FIFO logic)
                let newOpenPanels = [...state.openPanels, panelId];

                if (newOpenPanels.length > MAX_OPEN_PANELS) {
                    newOpenPanels = newOpenPanels.slice(-MAX_OPEN_PANELS);
                }

                return {
                    ...state,
                    openPanels: newOpenPanels,
                    // Reset collapsed when toggling
                    collapsed: false,
                };
            }
        }

        case ActionTypes.CloseAllPanels: {
            return {
                ...state,
                openPanels: [],
                collapsed: false,
            };
        }

        case ActionTypes.ToggleCollapsed: {
            return {
                ...state,
                collapsed: !state.collapsed,
            };
        }

        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
